/*
 * Copyright (c) 2020. American Express Company
 */

// third-party
import * as _ from 'lodash';
import React, { Component, ReactComponentElement } from 'react';
import Table from '@mui/material/Table/Table';
import TableBody from '@mui/material/TableBody/TableBody';
import TableCell from '@mui/material/TableCell/TableCell';
import TableHead from '@mui/material/TableHead/TableHead';
import TableRow from '@mui/material/TableRow/TableRow';
import { LocalDate } from '@js-joda/core';

// models and services
import { AccountModel } from 'src/types/account.model';
import { CategoryModel } from 'src/types/category.model';
import { IncomeSourceRuleModel } from 'src/types/income-source-rule.model';
import { RuleModel } from 'src/types/rule.model';
import { MenuItemModel, SASVariant } from 'src/types/view-models/search-and-select.view.model';
import { TransactionModel } from 'src/types/transaction.model';
import FormatService from '../../../../services/format.service';
import IncomeSourceRuleService from '../../../../services/income-source-rule.service';
import RuleService from '../../../../services/rule.service';
import TransactionService, { Frequency } from '../../../../services/transaction.service';
import NavigationService from '../../../../services/navigation.service';
import { BwAlertType } from 'src/types/view-models/bw-alert.view.model';
import UtilsService from '../../../../services/utils.service';
import CategoryService from '../../../../services/category.service';
import { IncomeSourceModel } from 'src/types/income-source.model';
import IncomeSourceService from '../../../../services/income-source.service';
import { TransactionField } from 'src/bw/project-jaeger/data/TransactionField.enum';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import LiteralsService from 'src/services/literals/LiteralsService';

// components
import {
    ButtonTertiary,
    GlyphInfo,
    GlyphRight,
    GlyphSortDown,
    GlyphSortUp,
    IconCancelCircle,
    IconClose,
    IconDollar,
    IconHide,
    IconThumbsUp,
    IconTransfer,
    IconTrash,
    Input,
    TooltipButton
} from '@americanexpress/dls-react';
import ClickableIcon from 'src/bw/clickable-icon/ClickableIcon';
import Dropdown from '../../../menus/Dropdown';
import FauxMenu from '../../../faux-menu/FauxMenu';
import OccurrenceMenu from 'src/bw/bw-drawer/OccurrenceMenu';
import RoundedDropdown from '../../../menus/RoundedDropdown';
import SearchAndSelect from '../../../search-and-select/SearchAndSelect';
import Typography from '../../../typography/Typography';
import Checkbox from 'src/components/dlsMods/checkbox/Checkbox';
import BwAlert from '../../../alert/BwAlert';
import BulkEdit from 'src/components/atomic/organisms/transaction/bulk-edit/BulkEdit';
import Tooltip from 'src/components/dlsMods/tooltip/Tooltip';

// icons and styles
import { ReactComponent as InfoIcon } from './info-icon.svg';
import styles from './UnifiedTransactionsTable.module.scss';
import UrlHelper from 'src/utils/url.helper';
import Selectors from 'src/utils/selectors';
import { RuleMatcher } from 'src/types/ruleMatcher.enum';
import { TransactionType } from 'src/types/transaction-type.enum';
import { matchesRule } from 'src/utils/rule.util';

export class UnifiedDrawerValues {
    expense: number;
    income: number;
    menuStyles: Object;
    menuItemModels: MenuItemModel[];
    selectedIncome: TransactionModel[];
    selectedExpense: TransactionModel[];
}

export const TransactionsTableContext = React.createContext({
    handleChangeIncomeSource: () => {},
    handleDisplayTransaction: () => {},
    handleIgnoreFutureTransaction: () => {},
    handleSortUnifiedTransaction: () => {},
    handleChangeTransactionGroup: () => {},
    recategorizeSelectedIncomes: () => {},
    recategorizeSelectedExpenses: () => {},
    changeFrequenceOfSelected: () => {},
    handleReloadUser: () => {},
    handleDeleteIncomeRule: () => {},
    handleDeleteRule: () => {}
});

export default class UnifiedTransactionsTable extends Component<any> {
    public override state = {
        anchorEl: null as any,
        showBulkEditBar: false,
        // editTransaction state
        selectedTransaction: null as TransactionModel,
        selectedTransactionRule: null as any, // either RuleModel or IncomeRuleModel
        selectedTransactionText: '',
        changeCategory: false,
        changeIncome: false,
        changeFrequency: false,
        changeToGroupUid: null as string,
        changeToIncomeUid: null as string,
        changeToFrequency: Frequency.TYPICAL,
        moveAll: false,
        // new category
        showNewCategory: false,
        showNewIncomeSource: false,
        newCategory: '' as string,
        newCategoryParentName: '' as string,
        newCategoryParentId: '' as string,
        occurrenceTransaction: null as TransactionModel
    };

    public override render(): ReactComponentElement<any> {
        const drawerProps: any = this.props.drawerProps;
        const selectedIncome: TransactionModel[] = _.filter(
            drawerProps.selectedTransactions,
            (transaction: TransactionModel) => {
                return transaction.type === TransactionType.TRANSACTION_INCOME;
            }
        );

        let nameSort: JSX.Element = null;
        let dateSort: JSX.Element = null;
        let amountSort: JSX.Element = null;
        let sortComp: JSX.Element = <GlyphSortDown className={styles.sort} />;
        let categorySort: JSX.Element = null;
        let occurrenceSort: JSX.Element = null;
        let typeSort: JSX.Element = null;

        const menuItemModels: MenuItemModel[] = SearchAndSelect.getMenuItemModels(
            _.sortBy(drawerProps.categories, 'name')
        );

        let reviewButton: JSX.Element = this.props.user.expenseTransactionsToReviewCount ? (
            <BwAlert
                text={`${this.props.user.expenseTransactionsToReviewCount} expense transactions to review.`}
                action="Review them now"
                type={BwAlertType.NEUTRAL}
                fullWidth={true}
                onClick={() => {
                    this.props.handleOpenReviewExpenseTransactions(false);
                }}
                key={'stepZeroIncome'}
            />
        ) : null;

        reviewButton =
            !reviewButton && this.props.user.incomeTransactionsToReviewCount ? (
                <BwAlert
                    text={`${this.props.user.incomeTransactionsToReviewCount} income transactions to review.`}
                    action="Review them now"
                    type={BwAlertType.NEUTRAL}
                    fullWidth={true}
                    onClick={() => {
                        this.props.handleOpenReviewExpenseTransactions(false);
                    }}
                    key={'stepZeroIncome'}
                />
            ) : null;

        const addCategoryDiv =
            this.state.showNewCategory || this.state.showNewIncomeSource ? (
                <div className={styles.newCategoryDialog}>
                    <Typography variant={TypographyVariants.HEADING_M} className="after16">
                        {this.state.showNewCategory
                            ? `Add new category to ${this.state.newCategoryParentName}`
                            : 'Add new income source'}
                    </Typography>
                    <Input
                        id="new-category"
                        className={styles.newCategory}
                        value={this.state.newCategory}
                        onChange={(e: any) => {
                            this.setState({ newCategory: e.target.value });
                        }}
                    />
                    <div className={styles.buttons}>
                        <ButtonTertiary
                            id="save"
                            onClick={async () => {
                                await this.newCategory();
                            }}
                        >
                            {this.state.showNewCategory ? 'Add subcategory' : 'Add income source'}
                        </ButtonTertiary>
                        <ButtonTertiary
                            borderless={true}
                            id="save"
                            onClick={() => {
                                this.setState({
                                    showNewCategory: false,
                                    showNewIncomeSource: false
                                });
                            }}
                        >
                            Cancel
                        </ButtonTertiary>
                    </div>
                </div>
            ) : null;

        const bulkEditBar = this.state.showBulkEditBar ? (
            <BulkEdit
                selectedTransactions={drawerProps.selectedTransactions}
                onClose={() => {
                    this.setState({ showBulkEditBar: false });
                    this.props.updateDrawer({ ...this.props.drawerProps, selectedTransactions: [] });
                }}
            />
        ) : null;

        let moveString: JSX.Element | null = null;
        if (drawerProps.selectedTransactions && drawerProps.selectedTransactions.length > 0) {
            let names: string[] = drawerProps.selectedTransactions.map(
                (transaction: TransactionModel) => transaction.displayName
            );
            names = _.uniq(names);

            if (names.length > 5) {
                moveString = UtilsService.formatListBold(names.slice(0, 5));
            } else if (drawerProps.selectedTransactions.length) {
                moveString = UtilsService.formatListBold(names);
            }
        }

        const changeIncome =
            this.state.changeIncome && drawerProps.selectedTransactions.length > 0 && selectedIncome.length > 0 ? (
                <div className={styles.changeIncome}>
                    <Typography variant={TypographyVariants.SUBTEXT} className="after24">
                        Recategorize transactions
                    </Typography>
                    <RoundedDropdown
                        value={this.state.changeToIncomeUid || 'none'}
                        onChange={(e) => {
                            if (Number(e.target.value) === -1) {
                                this.setState({
                                    showNewIncomeSource: true
                                });
                            } else {
                                this.setState({ changeToIncomeUid: e.target.value });
                            }
                        }}
                    >
                        <option key={'source'} value="none">
                            Income Source
                        </option>
                        {this.props.drawerProps.incomeSources.map((incomeSource: any) => {
                            return (
                                <option key={`incomeSource${incomeSource.incomeUid}`} value={incomeSource.incomeUid}>
                                    {incomeSource.name}
                                </option>
                            );
                        })}
                        <option value={-1}>New Income Source</option>
                    </RoundedDropdown>
                    <div className="clear after12" />
                    <Checkbox
                        className="left"
                        checked={this.state.moveAll}
                        onChange={() => this.setState({ moveAll: !this.state.moveAll })}
                    />
                    <Typography variant={TypographyVariants.BODY}>
                        {LiteralsService.getLiteral('UnifiedTransactionsTable.addRule1')}
                        {moveString}
                        {LiteralsService.getLiteral('UnifiedTransactionsTable.addRule2')}
                    </Typography>
                    <div className="clear" />
                    <div className="full-width">
                        <ButtonTertiary
                            disabled={!this.state.changeToIncomeUid}
                            onClick={() => {
                                this.props.recategorizeSelectedIncomes(
                                    this.state.changeToIncomeUid,
                                    this.state.moveAll
                                );
                                this.setState({ changeIncome: false });
                            }}
                            id="add-asset"
                        >
                            Recategorize
                        </ButtonTertiary>
                        <ButtonTertiary
                            borderless={true}
                            onClick={() => {
                                this.setState({
                                    changeIncome: false,
                                    showBulkEditBar: true
                                });
                            }}
                            id="add-asset"
                        >
                            Cancel
                        </ButtonTertiary>
                    </div>
                </div>
            ) : null;

        if (drawerProps.transactionSort) {
            if (drawerProps.transactionSort.order === -1) {
                sortComp = <GlyphSortUp className={styles.sort} />;
            }
            switch (drawerProps.transactionSort.sort) {
                case 'name':
                    nameSort = sortComp;
                    break;
                case 'amount':
                    amountSort = sortComp;
                    break;
                case 'date':
                    dateSort = sortComp;
                    break;
                case 'category':
                    categorySort = sortComp;
                    break;
                case 'type':
                    typeSort = sortComp;
                    break;
                case 'occurrence':
                    occurrenceSort = sortComp;
                    break;
            }
        }

        const transactions: TransactionModel[] = this.props.transactions;

        let editTransactionDiv = null;

        if (this.state.selectedTransaction) {
            const transaction: TransactionModel = this.state.selectedTransaction;
            const account: AccountModel = _.find(this.props.user.accounts, { id: transaction.accountId });

            let occurrenceName = 'Typical';
            if (transaction.ignore) {
                occurrenceName = 'Ignore';
            } else if (transaction.ignoreInFuture) {
                occurrenceName = 'One Time';
            }

            editTransactionDiv = (
                <div className={styles.editTransactionDialog}>
                    <div className="close right" data-cy={Selectors.Transactions.CLOSE}>
                        <ClickableIcon
                            icon={<IconClose />}
                            className={styles.closeEditModal}
                            onClick={async () => {
                                if (this.state.selectedTransactionRule) {
                                    if (this.state.selectedTransaction.type === 'TRANSACTION_EXPENSE') {
                                        if (this.state.selectedTransactionRule.groupUid) {
                                            await RuleService.createOrUpdateRule(this.state.selectedTransactionRule);
                                            await this.reloadTransactionsRule(
                                                drawerProps,
                                                this.state.selectedTransactionRule
                                            );
                                        }
                                    } else {
                                        if (this.state.selectedTransactionRule.incomeUid) {
                                            await IncomeSourceRuleService.createOrUpdateRule(
                                                this.state.selectedTransactionRule
                                            );
                                            await this.reloadTransactionsIncomeRule(
                                                drawerProps,
                                                this.state.selectedTransactionRule
                                            );
                                        }
                                    }
                                }
                                await TransactionService.updateTransaction(this.state.selectedTransaction);
                                this.setState({
                                    selectedTransaction: null,
                                    selectedTransactionRule: null
                                });
                            }}
                        />
                    </div>
                    <Typography variant={TypographyVariants.HEADING_S}>Transaction Details {transaction.id}</Typography>

                    <div id="transactionDetails" className={styles.mainPanel}>
                        <div className={`${styles.panel} ${styles.topPanel}`}>
                            <Typography variant={TypographyVariants.SUBTEXT_BOLD}>Name or nickname</Typography>
                            {transaction.nickName ? (
                                <ClickableIcon
                                    icon={<IconCancelCircle />}
                                    className={styles.resetNameIcon}
                                    onClick={() => {
                                        const transaction = this.state.selectedTransaction;
                                        transaction.nickName = null;
                                        this.setState({ selectedTransaction: transaction });
                                    }}
                                />
                            ) : null}
                            <Input
                                type="string"
                                value={this.state.selectedTransaction.nickName || this.state.selectedTransaction.name}
                                variant="outlined"
                                onChange={(e: any) => {
                                    const transaction = this.state.selectedTransaction;
                                    transaction.nickName = e.target.value;
                                    this.setState({ selectedTransaction: transaction });
                                }}
                            />

                            <div className="clear" />
                            <Typography variant={TypographyVariants.CAPTION}>{transaction.displayName}</Typography>
                        </div>
                        <div className={`${styles.panel} ${styles.topPanel} ${styles.amount}`}>
                            <Typography variant={TypographyVariants.SUBTEXT_BOLD} className={styles.amount}>
                                Amount
                            </Typography>
                            <Typography
                                variant={TypographyVariants.BODY}
                                className={transaction.amount < 0 ? 'positive' : 'negative'}
                            >
                                {FormatService.formatCurrency(-transaction.amount)}
                            </Typography>
                        </div>
                        <div className={styles.panel}>
                            <Typography variant={TypographyVariants.SUBTEXT_BOLD}>Account</Typography>
                            <Typography variant={TypographyVariants.BODY}>{account ? account.name : ''}</Typography>
                        </div>
                        <div className={styles.panel}>
                            <Typography variant={TypographyVariants.SUBTEXT_BOLD}>Date</Typography>
                            <Typography variant={TypographyVariants.BODY}>
                                {FormatService.formatDate(transaction.date)}
                            </Typography>
                        </div>
                        <div className={styles.panel}>
                            <Typography variant={TypographyVariants.SUBTEXT_BOLD}>Category</Typography>
                            {transaction.type === 'TRANSACTION_EXPENSE'
                                ? this.categoryPulldown(transaction, menuItemModels, -1, true)
                                : this.incomePulldown(transaction)}
                        </div>
                        <div className={styles.panel}>
                            <Typography variant={TypographyVariants.SUBTEXT_BOLD}>Frequency</Typography>
                            <FauxMenu
                                onClick={(e: any) => this.handleClickOccurrence(e, transaction)}
                                className={styles.occurrence}
                            >
                                {occurrenceName}
                            </FauxMenu>
                        </div>
                        {this.state.selectedTransactionRule ? (
                            <div className={styles.rulePanel}>
                                <Typography variant={TypographyVariants.SUBTEXT}>If </Typography>
                                <RoundedDropdown
                                    value={this.state.selectedTransactionRule.matcher}
                                    onChange={async (e: any) => {
                                        if (this.state.selectedTransaction.type === 'TRANSACTION_EXPENSE') {
                                            await this.changeRule(
                                                this.state.selectedTransactionRule,
                                                'matcher',
                                                e.target.value
                                            );
                                        } else {
                                            await this.changeIncomeRule(
                                                this.state.selectedTransactionRule,
                                                'matcher',
                                                e.target.value
                                            );
                                        }
                                    }}
                                    className={styles.matchSelect}
                                >
                                    <option value="contains">contains</option>
                                    <option value="startsWith">starts with</option>
                                    <option value="endsWith">ends with</option>
                                </RoundedDropdown>
                                <Input
                                    type="string"
                                    value={
                                        this.state.selectedTransaction.type === 'TRANSACTION_EXPENSE'
                                            ? this.state.selectedTransactionRule.transactionName
                                            : this.state.selectedTransactionRule.transactionNamePattern
                                    }
                                    variant="outlined"
                                    onChange={async (e: any) => {
                                        if (this.state.selectedTransaction.type === 'TRANSACTION_EXPENSE') {
                                            await this.changeRule(
                                                this.state.selectedTransactionRule,
                                                'transactionName',
                                                e.target.value
                                            );
                                        } else {
                                            await this.changeIncomeRule(
                                                this.state.selectedTransactionRule,
                                                'transactionNamePattern',
                                                e.target.value
                                            );
                                        }
                                    }}
                                />
                                <Typography variant={TypographyVariants.SUBTEXT}> move to </Typography>
                                {this.state.selectedTransaction.type === 'TRANSACTION_EXPENSE'
                                    ? this.ruleCategoryPulldown(menuItemModels)
                                    : this.incomeRulePulldown()}
                                <ClickableIcon
                                    icon={<IconTrash />}
                                    onClick={async () => {
                                        this.setState({ selectedTransactionRule: null });
                                        if (this.state.selectedTransaction.type === 'TRANSACTION_EXPENSE') {
                                            if (this.state.selectedTransactionRule.id) {
                                                this.props.handleDeleteRule(this.state.selectedTransactionRule);
                                            }
                                        } else {
                                            if (this.state.selectedTransactionRule.incomeRuleUid) {
                                                this.props.handleDeleteIncomeRule(this.state.selectedTransactionRule);
                                            }
                                        }
                                    }}
                                    className={styles.delete}
                                />
                            </div>
                        ) : (
                            <div className={styles.rulePanel}>
                                <Typography variant={TypographyVariants.SUBTEXT_BOLD}>Rule</Typography>
                                <Typography variant={TypographyVariants.CAPTION} className={styles.rule2}>
                                    Rules define how a transaction gets categorized.
                                </Typography>
                                <ButtonTertiary
                                    onClick={async () => {
                                        await this.addRule();
                                    }}
                                >
                                    Add rule
                                </ButtonTertiary>
                            </div>
                        )}
                        <div className="clear" />
                    </div>
                </div>
            );
        }

        const transactionRows = !transactions.length ? (
            <TableRow />
        ) : (
            transactions.map((transaction: any, index: number) => {
                let occurrenceName = 'Typical';
                let occurrenceIcon = <IconTransfer filled className={styles.occurrenceIcon} />;

                if (transaction.ignore) {
                    occurrenceName = 'Ignore';
                    occurrenceIcon = <IconHide filled className={styles.occurrenceIcon} />;
                } else if (transaction.ignoreInFuture) {
                    occurrenceName = 'One Time';
                    occurrenceIcon = <IconDollar filled className={styles.occurrenceIcon} />;
                }

                let account: AccountModel = _.find(this.props.user.accounts, { id: transaction.accountId });

                if (!account) {
                    account = _.find(this.props.drawerProps.accounts, { id: transaction.accountId });
                }

                const checked = this.props.selectedTransactions?.includes(transaction);
                return (
                    <TableRow key={transaction.id} className={`row${transaction.id}`}>
                        <TableCell scope="row" className="desktop" key={`check-${index}`}>
                            <Checkbox
                                checked={checked}
                                onChange={() => {
                                    this.selectTransaction(transaction, !checked);
                                }}
                                data-cy={Selectors.Transactions.TRANSACTION_TABLE_CHECKBOX}
                            />
                        </TableCell>
                        {this.props.review ? (
                            <TableCell className="review-column" key={`review-${index}`}>
                                <Tooltip
                                    triggerElement={<TooltipButton info icon={<GlyphInfo />} />}
                                    className="info"
                                    info
                                >
                                    {transaction.reviewReason}
                                </Tooltip>
                            </TableCell>
                        ) : null}
                        <TableCell
                            scope="row"
                            className={styles.nameColumn}
                            key={`name-${index}`}
                            data-cy={Selectors.Transactions.NAME_COLUMN}
                        >
                            <Typography variant={TypographyVariants.LEGAL_PRIMARY} className="date notDesktop">
                                {FormatService.formatDate(transaction.date)}
                            </Typography>
                            <Typography
                                variant={TypographyVariants.SUBTEXT}
                                className="name link"
                                id="nameLink"
                                onClick={async () => await this.handleEditTransaction(transaction)}
                            >
                                {FormatService.abbreviate(transaction.displayName, 128)}
                            </Typography>
                            <Typography variant={TypographyVariants.LEGAL_PRIMARY}>
                                {transaction.displayName}
                            </Typography>
                            <Typography
                                variant={TypographyVariants.LEGAL_PRIMARY}
                                className={styles.textDefault}
                                data-cy={Selectors.PlanAhead.ACCOUNT_NICKNAME}
                            >
                                {account ? account.name : ''}
                            </Typography>
                            {this.props.user.attributes.debugCategories ? (
                                <Typography variant={TypographyVariants.CAPTION} className={styles.categories}>
                                    {transaction.categories}
                                </Typography>
                            ) : null}
                        </TableCell>
                        <TableCell align="right" key={`amount-${index}`} className={styles.amountColumn}>
                            <Typography
                                variant={TypographyVariants.SUBTEXT}
                                className={transaction.amount < 0 ? 'positive' : 'negative'}
                            >
                                {FormatService.formatCurrency(-transaction.amount)}
                            </Typography>
                        </TableCell>
                        <TableCell align="left" key={`date-${index}`} data-cy={Selectors.Transactions.DATE_COLUMN}>
                            <Typography variant={TypographyVariants.SUBTEXT}>
                                {FormatService.formatDate(transaction.date)}
                            </Typography>
                        </TableCell>
                        <TableCell
                            align="left"
                            key={`type-${index}`}
                            data-cy={Selectors.Transactions.TYPE_COLUMN}
                            className="largeDesktop"
                        >
                            <Typography variant={TypographyVariants.SUBTEXT}>
                                {transaction.type === 'TRANSACTION_EXPENSE' ? 'Expense' : 'Income'}
                            </Typography>
                        </TableCell>
                        <TableCell align="left" key={`group-${index}`} className="bigphone" data-cy="name-column">
                            {transaction.type === 'TRANSACTION_EXPENSE'
                                ? this.categoryPulldown(transaction, menuItemModels, index, false)
                                : this.incomePulldown(transaction)}
                        </TableCell>
                        <TableCell
                            key={`occurrence-${index}`}
                            align="left"
                            data-cy="occurrence-column"
                            className="tablet"
                        >
                            <FauxMenu
                                onClick={(e: any) => this.handleClickOccurrence(e, transaction)}
                                className={styles.occurrenceMenu}
                            >
                                {occurrenceIcon}
                                {occurrenceName}
                            </FauxMenu>
                        </TableCell>
                        <TableCell
                            id={`transaction-edit-${index}`}
                            align="left"
                            key={`edit-${index}`}
                            className="edit-column desktop"
                        >
                            <Typography
                                variant={TypographyVariants.SUBTEXT}
                                className="link add-rule"
                                id="linkAddRule"
                                onClick={async () => await this.handleEditTransaction(transaction)}
                            >
                                <InfoIcon className={styles.info} />
                                Info
                            </Typography>
                        </TableCell>
                        {this.props.isReview ? (
                            <TableCell align="left">
                                <ClickableIcon
                                    icon={<IconThumbsUp />}
                                    onClick={() => {
                                        this.props.approveTransaction(transaction);
                                    }}
                                />
                            </TableCell>
                        ) : null}
                        <TableCell
                            id={`transaction-key-${index}`}
                            align="left"
                            key={`arrow-${index}`}
                            className="edit-column notDesktop"
                            onClick={() => {
                                void this.handleEditTransaction(transaction);
                            }}
                        >
                            <ClickableIcon
                                icon={<GlyphRight />}
                                onClick={async () => await this.handleEditTransaction(transaction)}
                                className={styles.keyArrowRight}
                            />
                        </TableCell>
                    </TableRow>
                );
            })
        );

        let headerRow: JSX.Element = (
            <React.Fragment>
                <TableCell scope="row">
                    <Checkbox
                        checked={
                            this.props.selectedTransactions && transactions
                                ? this.props.selectedTransactions.length === transactions.length
                                : false
                        }
                        onChange={() =>
                            this.selectAllTransactions(this.props.selectedTransactions?.length !== transactions?.length)
                        }
                        data-cy={Selectors.Transactions.SELECT_ALL_CHECKBOX}
                    />
                </TableCell>
                {this.props.review ? <TableCell /> : null}
                <TableCell
                    className="link"
                    onClick={() => {
                        this.props.handleSortUnifiedTransaction(TransactionField.NAME);
                    }}
                >
                    <Typography variant={TypographyVariants.CAPTION} className={styles.header}>
                        Name&nbsp;{nameSort}
                    </Typography>
                </TableCell>
                <TableCell
                    className="link"
                    onClick={() => {
                        this.props.handleSortUnifiedTransaction(TransactionField.AMOUNT);
                    }}
                    align="right"
                >
                    <Typography variant={TypographyVariants.CAPTION} className={styles.header}>
                        Amount&nbsp;{amountSort}
                    </Typography>
                </TableCell>
                <TableCell
                    className="link desktop"
                    onClick={() => {
                        this.props.handleSortUnifiedTransaction(TransactionField.DATE);
                    }}
                    align="left"
                >
                    <Typography variant={TypographyVariants.CAPTION} className="header ">
                        Date&nbsp;{dateSort}
                    </Typography>
                </TableCell>
                <TableCell
                    className="link desktop"
                    onClick={() => {
                        this.props.handleSortUnifiedTransaction(TransactionField.TYPE);
                    }}
                    align="left"
                >
                    <Typography variant={TypographyVariants.CAPTION} className={styles.header}>
                        Type&nbsp;{typeSort}
                    </Typography>
                </TableCell>
                <TableCell
                    className="link"
                    onClick={() => {
                        this.props.handleSortUnifiedTransaction(TransactionField.CATEGORY);
                    }}
                    align="left"
                >
                    <Typography variant={TypographyVariants.CAPTION} className={styles.header}>
                        Category / Source&nbsp;{categorySort}
                    </Typography>
                </TableCell>
                <TableCell
                    className="link"
                    onClick={() => {
                        this.props.handleSortUnifiedTransaction(TransactionField.OCCURRENCE);
                    }}
                    align="left"
                >
                    <Typography variant={TypographyVariants.CAPTION} className={styles.header}>
                        Frequency&nbsp;{occurrenceSort}
                    </Typography>
                </TableCell>
                <TableCell />
                {this.props.isReview ? (
                    <TableCell className="link" align="left">
                        <Typography variant={TypographyVariants.CAPTION} className={styles.header}>
                            Approve
                        </Typography>
                    </TableCell>
                ) : null}
            </React.Fragment>
        );

        if (this.props.addHeaderColumns) {
            headerRow = this.props.addHeaderColumns(headerRow);
        }

        const table: JSX.Element = transactions.length ? (
            <React.Fragment>
                <Table className={styles.unifiedTransactionsTable} data-cy={Selectors.Transactions.TRANSACTIONS_TABLE}>
                    <TableHead className={styles.desktop}>
                        {this.props.requireHeader ? <TableRow>{headerRow}</TableRow> : null}
                    </TableHead>
                    <TableBody>{transactionRows}</TableBody>
                </Table>
                <OccurrenceMenu
                    onSelect={this.handleChangeOccurrence.bind(this)}
                    anchorEl={this.state.anchorEl}
                    onDismiss={this.handleCloseOccurrence.bind(this)}
                    type="expense"
                />
            </React.Fragment>
        ) : this.props.isReview ? (
            <div className={styles.transactionReviewDone}>
                <div className={styles.reviewDoneCta}>
                    <Typography variant={TypographyVariants.HEADING_M} className={styles.nicelyDone}>
                        Nicely Done
                    </Typography>
                    <Typography variant={TypographyVariants.SUBTEXT} className="after16">
                        No more transactions to review.
                    </Typography>
                    <ButtonTertiary className="after16" onClick={() => UrlHelper.replacePath('drawer/transactions')}>
                        View all transactions
                    </ButtonTertiary>
                    <Typography
                        variant={TypographyVariants.CAPTION}
                        className="link back-to-timeline"
                        onClick={() => this.props.handleBack()}
                    >
                        Back to timeline
                    </Typography>
                </div>
            </div>
        ) : (
            <div className={styles.noTransactions}>
                <Typography variant={TypographyVariants.SUBTEXT} className={styles.after12}>
                    No transactions match this criteria.
                </Typography>
                <ButtonTertiary
                    id="save"
                    onClick={async () => {
                        await this.clearAllFilters();
                    }}
                >
                    Clear all filters
                </ButtonTertiary>
            </div>
        );

        return (
            <div className={styles.transactionsDiv} data-cy={Selectors.Transactions.TRANSACTION_TH}>
                {addCategoryDiv}
                {bulkEditBar}
                {changeIncome}
                {reviewButton}
                {editTransactionDiv}
                {table}
            </div>
        );
    }

    private async handleEditTransaction(transaction: TransactionModel): Promise<void> {
        this.setState({
            selectedTransaction: transaction
        });

        let rule: any;

        if (transaction.type === 'TRANSACTION_EXPENSE') {
            const rules = await RuleService.getRules();
            rule = _.find(rules, (rule) => {
                return matchesRule(transaction, rule);
            });
        } else {
            const incomeRules = await IncomeSourceRuleService.getRules();
            rule = _.find(incomeRules, (rule) => {
                return IncomeSourceRuleService.matchesRule(transaction, rule);
            });
        }

        this.setState({
            selectedTransactionRule: rule
        });

        this.props.updateDrawer({
            ...this.props.drawerProps,
            selectedTransaction: transaction
        });
    }

    private async handleChangeOccurrence(occurrence: string): Promise<void> {
        this.handleCloseOccurrence();
        const transaction = this.state.occurrenceTransaction;
        switch (occurrence) {
            case Frequency.TYPICAL:
                if (transaction.ignoreInFuture) {
                    await this.props.handleIgnoreFutureTransaction(transaction);
                }
                if (transaction.ignore) {
                    await this.props.handleDisplayTransaction(transaction);
                }
                break;

            case Frequency.ONETIME:
                if (!transaction.ignoreInFuture) {
                    await this.props.handleIgnoreFutureTransaction(transaction);
                }
                if (transaction.ignore) {
                    await this.props.handleDisplayTransaction(transaction);
                }
                break;

            case Frequency.IGNORE:
                if (!transaction.ignore) {
                    await this.props.handleDisplayTransaction(transaction);
                }
                break;
        }
        await this.props.handleReloadUser();
    }

    private handleClickOccurrence(event: any, transaction: TransactionModel): void {
        this.setState({
            anchorEl: event.currentTarget,
            occurrenceTransaction: transaction
        });
    }

    private handleCloseOccurrence(): void {
        this.setState({
            anchorEl: null
        });
    }

    private async newCategory(): Promise<void> {
        if (this.state.showNewCategory) {
            await CategoryService.createCategory({
                name: this.state.newCategory,
                parentId: this.state.newCategoryParentId
            } as CategoryModel);

            const categories: CategoryModel[] = await CategoryService.getCategories();

            if (this.state.occurrenceTransaction) {
                const category: CategoryModel = categories.find((category: CategoryModel) => {
                    return (
                        category.name === this.state.newCategory && category.parentId === this.state.newCategoryParentId
                    );
                });
                await this.props.handleChangeTransactionGroup(this.state.occurrenceTransaction, category.id, false);
            } else {
                this.props.updateDrawer({
                    ...this.props.drawerProps,
                    categories
                });
            }
        } else {
            const newIncome: IncomeSourceModel = new IncomeSourceModel();
            newIncome.name = this.state.newCategory;
            await IncomeSourceService.createIncomeSource(newIncome);

            const incomeSources: IncomeSourceModel[] = _.sortBy(
                await IncomeSourceService.getIncomeSources(),
                (i) => i.name
            );

            this.props.updateDrawer({
                ...this.props.drawerProps,
                incomeSources
            });
        }

        this.setState({
            newCategory: '',
            showNewCategory: false,
            showNewIncomeSource: false,
            occurrenceTransaction: null
        });
    }

    private openNewCategory(menuItem: MenuItemModel, transaction?: TransactionModel): void {
        this.setState({
            showNewCategory: true,
            newCategoryParentId: menuItem.value,
            newCategoryParentName: menuItem.label,
            transaction: transaction || this.state.occurrenceTransaction
        });
    }

    private selectTransaction(transaction: TransactionModel, checked: boolean): void {
        let selectedTransactions: TransactionModel[] = this.props.drawerProps.selectedTransactions || [];
        let showBulkEditBar = this.state.showBulkEditBar;

        if (checked) {
            selectedTransactions.push(transaction);
            showBulkEditBar = true;
        } else {
            selectedTransactions = _.filter(selectedTransactions, (aTransaction) => aTransaction.id !== transaction.id);
            if (!selectedTransactions.length) {
                showBulkEditBar = false;
            }
        }

        this.setState({
            showBulkEditBar
        });

        this.props.updateDrawer({
            ...this.props.drawerProps,
            selectedTransactions
        });
    }

    private selectAllTransactions(checked: boolean): void {
        let selectedTransactions: TransactionModel[] = [];
        let showBulkEditBar = false;

        if (checked) {
            selectedTransactions = this.props.drawerProps.displayedTransactions;
            showBulkEditBar = true;
        }

        this.setState({
            showBulkEditBar
        });

        this.props.updateDrawer({
            ...this.props.drawerProps,
            selectedTransactions
        });
    }

    private addCategory(parent: MenuItemModel): void {
        this.setState({
            showNewCategory: true,
            newCategoryParentName: parent.label,
            newCategoryParentId: parent.value
        });
    }

    private setTransaction(transaction: TransactionModel): void {
        this.setState({
            occurrenceTransaction: transaction
        });
    }

    private addNewIncomeSource(): void {
        this.setState({ showNewIncomeSource: true });
    }

    private async clearAllFilters(): Promise<void> {
        let drawerProps: any = { ...this.props.drawerProps };

        drawerProps.filters = {};
        drawerProps.page = 1;
        drawerProps.search = null;
        drawerProps.groupUid = null;
        drawerProps.type = null;
        drawerProps.incomeSource = null;
        drawerProps.startDate = null;
        drawerProps.endDate = null;

        NavigationService.goTo(this.props.history, '/drawer/transactions?page=1', 'Transactions drawer').catch(
            console.error
        );

        drawerProps.transactions = await TransactionService.getTransactions(
            this.props.user.cashFlow[0].startDate,
            LocalDate.now().plusDays(1)
        );

        drawerProps = this.props.updateFilteredTransactions(drawerProps);
        this.props.updateDrawer(drawerProps);
    }

    private categoryPulldown(
        transaction: TransactionModel,
        menuItemModels: MenuItemModel[],
        index?: number,
        fullWidth?: boolean
    ): JSX.Element {
        const category: CategoryModel = _.find(this.props.drawerProps.categories, { id: transaction.groupUid });

        return (
            <SearchAndSelect
                name={transaction.group}
                title="Category"
                items={menuItemModels}
                variant={SASVariant.ROUNDED}
                expanded={[category.parentId]}
                selected={transaction.groupUid}
                onClick={(item: MenuItemModel) => {
                    if (Number(item.value) === -1) {
                        this.setState({
                            showNewExpenseCategory: true
                        });
                    } else {
                        this.props.handleChangeTransactionGroup(transaction, item.value, false);
                    }
                }}
                className={styles.categoryMenu}
                addCallback={this.addCategory.bind(this)}
                value={transaction}
                setToValue={this.setTransaction.bind(this)}
                fullWidth={fullWidth}
                addName="Add custom subcategory"
            />
        );
    }

    private ruleCategoryPulldown(menuItemModels: MenuItemModel[]): JSX.Element {
        const category: CategoryModel = this.state.selectedTransactionRule.groupUid
            ? _.find(this.props.drawerProps.categories, { id: this.state.selectedTransactionRule.groupUid })
            : null;

        return (
            <SearchAndSelect
                name={category ? category.name : 'Select Category'}
                title="Category"
                className={styles.groupSelect}
                items={menuItemModels}
                variant={SASVariant.ROUNDED}
                expanded={category ? [category.parentId] : []}
                selected={this.state.selectedTransactionRule.groupUi}
                onClick={(item: MenuItemModel) => {
                    if (Number(item.value) === -1) {
                        this.setState({
                            showNewExpenseCategory: true
                        });
                    } else {
                        const rule = this.state.selectedTransactionRule;
                        rule.groupUid = item.value;
                        this.setState({
                            selectedTransactionRule: rule
                        });
                    }
                }}
                addCallback={this.props.addCallback}
                addName="Add custom subcategory"
            />
        );
    }

    private incomePulldown(transaction: TransactionModel): JSX.Element {
        return (
            <RoundedDropdown
                value={transaction.incomeUid || undefined}
                onChange={(e: any) => {
                    if (Number(e.target.value) === -1) {
                        this.addNewIncomeSource();
                    } else {
                        this.props.handleChangeIncomeSource(transaction, e.target.value, false);
                    }
                }}
                className={styles.incomeSelect}
            >
                {this.props.drawerProps.incomeSources.map((incomeSource: any) => {
                    return (
                        <option key={`incomeSource${incomeSource.incomeUid}`} value={incomeSource.incomeUid}>
                            {incomeSource.name}
                        </option>
                    );
                })}
                <option value={-1}>New Income Source</option>
            </RoundedDropdown>
        );
    }

    private incomeRulePulldown(): JSX.Element {
        return (
            <Dropdown
                value={this.state.selectedTransactionRule.incomeUid || undefined}
                onChange={(e: any) => {
                    if (Number(e.target.value) === -1) {
                        this.props.addNewIncomeSource();
                    } else {
                        const rule = this.state.selectedTransactionRule;
                        rule.incomeUid = e.target.value;
                        this.setState({
                            selectedTransactionRule: rule
                        });
                    }
                }}
                className={styles.incomeSelect}
            >
                <option value={null}>Select Income Source</option>
                {this.props.drawerProps.incomeSources.map((incomeSource: any) => {
                    return (
                        <option key={`incomeSource${incomeSource.incomeUid}`} value={incomeSource.incomeUid}>
                            {incomeSource.name}
                        </option>
                    );
                })}
                <option value={-1}>New Income Source</option>
            </Dropdown>
        );
    }

    private async changeRule(rule: RuleModel, field: string, value: string): Promise<void> {
        (rule as any)[field] = value;
        this.setState({ selectedTransactionRule: rule });
    }

    private async changeIncomeRule(rule: IncomeSourceRuleModel, field: string, value: string): Promise<void> {
        (rule as any)[field] = value;
        this.setState({ selectedTransactionRule: rule });
    }

    private async addRule(): Promise<void> {
        if (this.state.selectedTransaction) {
            if (this.state.selectedTransaction.type === 'TRANSACTION_INCOME') {
                const newRule = new IncomeSourceRuleModel();
                newRule.matcher = RuleMatcher.CONTAINS;
                newRule.transactionNamePattern = this.state.selectedTransaction.name;
                newRule.incomeUid = null;

                this.setState({
                    selectedTransactionRule: newRule
                });
            } else {
                const newRule = new RuleModel();
                newRule.priority = 0;
                newRule.transactionName = this.state.selectedTransaction.name;
                newRule.matcher = RuleMatcher.CONTAINS;

                this.setState({
                    selectedTransactionRule: newRule
                });
            }
        }
    }

    public async reloadTransactionsRule(drawerProps: any, rule: RuleModel): Promise<void> {
        const transactions = drawerProps.transactions;
        for (const transaction of transactions) {
            if (matchesRule(transaction, rule)) {
                transaction.groupUid = rule.groupUid;
            }
        }

        drawerProps.transactions = transactions;
        drawerProps = this.props.updateFilteredTransactions(drawerProps);
        this.props.updateDrawer(drawerProps);
    }

    public async reloadTransactionsIncomeRule(drawerProps: any, rule: IncomeSourceRuleModel): Promise<void> {
        const transactions = drawerProps.transactions;
        for (const transaction of transactions) {
            if (IncomeSourceRuleService.matchesRule(transaction, rule)) {
                transaction.incomeUid = rule.incomeUid;
            }
        }

        drawerProps.transactions = transactions;
        drawerProps = this.props.updateFilteredTransactions(drawerProps);
        this.props.updateDrawer(drawerProps);
    }
}
